import { IconButton, Menu, Tooltip, Typography, toast } from '@neo4j-ndl/react';
import { EllipsisHorizontalIconOutline, ExternalLinkIcon } from '@neo4j-ndl/react/icons';
import { MODAL_TYPE, useModal } from '@nx/state';
import { type DbmsInfo, useOpenDirectoryMutation, useOpenFileMutation } from '@nx/state/desktop';
import { isNotNullish } from '@nx/stdlib';
import type { PropsWithChildren } from 'react';
import { useRef, useState } from 'react';

import { useToggleInstanceRunStatus } from '../hooks/use-toggle-instance-run-status';
import type { InstanceDetailsTab } from '../instance-details';

const MenuTooltip = ({ children, title }: PropsWithChildren<{ title?: string }>) => {
  return (
    <Tooltip type="simple" placement={'left'}>
      <Tooltip.Trigger hasButtonWrapper>{children}</Tooltip.Trigger>
      {isNotNullish(title) && <Tooltip.Content className="n-body-small">{title}</Tooltip.Content>}
    </Tooltip>
  );
};

export const MoreMenu = ({
  instance,
  onInstanceDetailsClick,
}: {
  instance: DbmsInfo;
  onInstanceDetailsClick: (initialTab?: InstanceDetailsTab) => void;
}) => {
  const menuRef = useRef(null);
  const { isStopped, isStarted } = useToggleInstanceRunStatus(instance);
  const [openDirectory] = useOpenDirectoryMutation();
  const [openFile] = useOpenFileMutation();

  const createDatabaseDumpModal = useModal(MODAL_TYPE.DBMS_DB_CREATE_DUMP);
  const importDatabaseDumpModal = useModal(MODAL_TYPE.DBMS_DB_IMPORT_DUMP);
  const dbmsCloneModal = useModal(MODAL_TYPE.DBMS_CLONE);
  const dbmsDeleteModal = useModal(MODAL_TYPE.DBMS_DELETE);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <IconButton ref={menuRef} ariaLabel="More instance actions" onClick={handleClick} size="medium">
        <EllipsisHorizontalIconOutline className="h-full w-full" />
      </IconButton>

      <Menu isOpen={isMenuOpen} anchorRef={menuRef} onClose={handleClick}>
        <Menu.Items>
          <Menu.Item
            title="Overview"
            onClick={() => {
              onInstanceDetailsClick('overview');
              setIsMenuOpen(false);
            }}
          ></Menu.Item>
          <Menu.Item
            title="Upgrade"
            onClick={() => {
              onInstanceDetailsClick('upgrade');
              setIsMenuOpen(false);
            }}
          ></Menu.Item>
          <Menu.Item
            title="Plugins"
            onClick={() => {
              onInstanceDetailsClick('plugins');
              setIsMenuOpen(false);
            }}
          ></Menu.Item>
          <Menu.Divider />
          <MenuTooltip title={isStarted ? 'Stop instance to load database' : undefined}>
            <Menu.Item
              title="Load database from file"
              description="Supports .dump, .tar, .backup"
              onClick={() => {
                importDatabaseDumpModal.open({ instance, rootPath: instance.rootPath });
                setIsMenuOpen(false);
              }}
              isDisabled={isStarted}
            />
          </MenuTooltip>
          <MenuTooltip title={isStarted ? 'Stop instance to export .dump' : undefined}>
            <Menu.Item
              title="Export databases to .dump"
              onClick={() => {
                createDatabaseDumpModal.open({ dbmsId: instance.id, rootPath: instance.rootPath });
                setIsMenuOpen(false);
              }}
              isDisabled={isStarted}
            />
          </MenuTooltip>
          <Menu.Item
            title="Open dumps folder"
            onClick={() => {
              openDirectory([instance.rootPath ?? '', 'data', 'dumps'])
                .unwrap()
                .catch(() => {
                  toast.danger('The dumps folder does not exist, export databases to create it.', {
                    isCloseable: true,
                    actionLabel: isStopped ? 'Export' : undefined,
                    onAction: () => {
                      createDatabaseDumpModal.open({ dbmsId: instance.id, rootPath: instance.rootPath });
                      setIsMenuOpen(false);
                    },
                    shouldCloseOnAction: true,
                  });
                });
            }}
          />
          <Menu.Divider />
          <Menu.Item
            title="Open neo4j.log"
            onClick={() => {
              openFile([instance.rootPath ?? '', 'logs', 'neo4j.log'])
                .unwrap()
                .catch((e) => {
                  toast.danger(`There was an error opening the neo4j.log file. ${e}`, {
                    isCloseable: true,
                  });
                });
            }}
          />
          <Menu.Item
            title="Open neo4j.conf"
            onClick={() => {
              openFile([instance.rootPath ?? '', 'conf', 'neo4j.conf'])
                .unwrap()
                .catch((e) => {
                  toast.danger(`There was an error opening the neo4j.conf file. ${e}`, {
                    isCloseable: true,
                  });
                });
            }}
          />
          <Menu.Divider />
          <MenuTooltip title={isStarted ? 'Stop instance to clone' : undefined}>
            <Menu.Item
              title="Clone"
              onClick={() => {
                dbmsCloneModal.open({ instance });
                setIsMenuOpen(false);
              }}
              isDisabled={isStarted}
            />
          </MenuTooltip>
          <Menu.Item
            title={
              <div className="flex items-center gap-3">
                <Typography variant="body-medium">Migrate to Neo4j AuraDB</Typography>
                <ExternalLinkIcon />
              </div>
            }
            onClick={() => {
              open('https://neo4j.com/docs/aura/tutorials/migration/', '_blank');
            }}
          />
          <Menu.Item
            title="Delete"
            className="text-palette-danger-text"
            isDisabled={false}
            onClick={() => {
              dbmsDeleteModal.open({ instance });
              setIsMenuOpen(false);
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
};
